import { DisplayOnlyChirpContentBlockTypename, TextChirpContentBlockTypename, ToolUseChirpContentBlockTypename, ToolResultChirpContentBlockTypename, PendingToolResultChirpContentBlockTypename, ContextChirpContentBlockTypename } from '../../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineStreamingService';
const TURN_COMPLETE_REASONS = new Set(['END_TURN', 'MAX_TOKENS', 'REFUSAL', 'ERROR', 'CANCELLED', 'ITERATION_LIMIT', 'ITERATION_TIMEOUT', 'TERMINAL_TOOL']);
export function isTurnComplete(stopReason) {
  if (!stopReason) return false;
  return TURN_COMPLETE_REASONS.has(stopReason);
}
export function isAwaitingExternalTool(stopReason) {
  return stopReason === 'EXTERNAL_TOOL';
}
export function isAwaitingApproval(stopReason) {
  return stopReason === 'TOOLS_REQUIRE_APPROVAL';
}
export function isAwaitingToolResult(stopReason) {
  return stopReason === 'AWAITING_TOOL_RESULT';
}
export function wrapTextInput(text) {
  return [{
    __typename: TextChirpContentBlockTypename,
    text
  }];
}
const HIDDEN_CONTEXT_RE = /^<context hidden="true">\n([\s\S]*?)\n<\/context>\n\n/;
export function buildHiddenContextBlock(context) {
  const lines = ['Custom Context:', ...Object.entries(context).map(([key, value]) => {
    const valueStr = typeof value === 'string' ? value : JSON.stringify(value);
    return `- ${key}: ${valueStr}`;
  })];
  return `<context hidden="true">\n${lines.join('\n')}\n</context>\n\n`;
}
export function stripHiddenContext(text) {
  return text.replace(HIDDEN_CONTEXT_RE, '');
}
export function extractHiddenContext(text) {
  const match = HIDDEN_CONTEXT_RE.exec(text);
  if (!match) return null;
  const raw = match[1];
  const entries = [];
  for (const line of raw.split('\n')) {
    const entryMatch = /^- (\S+): (.+)$/.exec(line);
    if (entryMatch) {
      entries.push({
        key: entryMatch[1],
        value: entryMatch[2]
      });
    }
  }
  return {
    raw,
    entries
  };
}
export const OPTIMISTIC_ID_PREFIX = 'optimistic-';
export function createOptimisticUserMessage(content, fileReferences) {
  const textBlocks = typeof content === 'string' ? wrapTextInput(stripHiddenContext(content)) : content;
  const blocks = fileReferences !== null && fileReferences !== void 0 && fileReferences.length ? [...textBlocks, {
    __typename: DisplayOnlyChirpContentBlockTypename,
    value: fileReferences,
    displayType: 'file-references',
    id: ''
  }] : textBlocks;
  return {
    id: `${OPTIMISTIC_ID_PREFIX}${Date.now()}`,
    role: 'USER',
    content: blocks,
    timestamp: Date.now(),
    inputTokensUsed: 0,
    outputTokensUsed: 0,
    cacheCreationInputTokens: 0,
    cacheReadInputTokens: 0,
    timing: {
      initiatedAt: Date.now()
    },
    extraMetadata: {}
  };
}
export function isHumanUserMessage(message) {
  return message.role === 'USER' && message.content.length > 0 && message.content.every(block => block.__typename !== ToolResultChirpContentBlockTypename && block.__typename !== PendingToolResultChirpContentBlockTypename && block.__typename !== ContextChirpContentBlockTypename);
}
const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);
export function isTurnTerminal(status) {
  return TERMINAL_STATUSES.has(status);
}
export function turnStatusToChatStatus(status) {
  switch (status) {
    case 'COMPLETED':
    case 'CANCELLED':
      return 'READY';
    case 'FAILED':
      return 'ERROR';
    case 'PENDING':
    case 'IN_PROGRESS':
      return 'STREAMING';
    case 'AWAITING_TOOL_RESULT':
      return 'AWAITING_TOOL';
    default:
      return 'STREAMING';
  }
}
export function isActiveStatus(status) {
  return status === 'STREAMING' || status === 'AWAITING_TOOL' || status === 'AWAITING_APPROVAL';
}
export function unwrapTurnMessages(records) {
  return records.map(r => r.messageContent);
}

// Unwraps turn message records and reverses from DESC (API default) to
// chronological ASC order. Builds a messageId → turnId map alongside.
export function unwrapConversationRecords(records) {
  const turnIdMap = new Map();
  const messages = [...records].reverse().map(record => {
    turnIdMap.set(record.messageContent.id, record.turnId);
    return record.messageContent;
  });
  return {
    messages,
    turnIdMap
  };
}
export function isTurnActive(messages) {
  if (messages.length === 0) return false;
  const lastMsg = messages[messages.length - 1];
  if (lastMsg.role === 'USER') return true;
  if (lastMsg.role === 'ASSISTANT') {
    const reason = lastMsg.stopReason;
    return !isTurnComplete(reason);
  }
  return false;
}
export function mapBreezeRpcError(error) {
  if (error && typeof error === 'object' && 'errorType' in error) {
    var _rpcError$chirpErrorM;
    const rpcError = error;
    return {
      type: rpcError.errorType,
      message: (_rpcError$chirpErrorM = rpcError.chirpErrorMessage) !== null && _rpcError$chirpErrorM !== void 0 ? _rpcError$chirpErrorM : `RPC error: ${String(rpcError.errorType)}`,
      cause: rpcError
    };
  }
  return {
    type: 'UNKNOWN',
    message: error instanceof Error ? error.message : 'An error occurred',
    cause: error
  };
}
export function tryFormatContent(raw) {
  try {
    const parsed = JSON.parse(raw);
    return JSON.stringify(parsed, null, 2);
  } catch (_unused) {
    return raw;
  }
}
export function toChatError(err, fallbackMessage = 'An error occurred') {
  return err && typeof err === 'object' && 'type' in err ? err : {
    type: 'UNKNOWN',
    message: err instanceof Error ? err.message : fallbackMessage,
    cause: err
  };
}
export function findLastAssistantMessage(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'ASSISTANT') return messages[i];
  }
  return undefined;
}

/**
 * Returns the correct ID to use when building a tool result for this tool call.
 * Mirrors ToolUseContentBlockCore.getIdForResult() on the Java side.
 *
 * OpenAI's Responses API uses separate `id` (item identifier) and `callId`
 * (function call identifier) fields. Tool results must reference `callId`.
 * Anthropic uses a single `id` for both. This function picks the right one.
 */
export function getToolUseIdForResult(block) {
  var _block$callId;
  return (_block$callId = block.callId) !== null && _block$callId !== void 0 ? _block$callId : block.id;
}
export function extractToolUseBlocks(messages) {
  const lastAssistantMessage = findLastAssistantMessage(messages);
  if (!lastAssistantMessage) return null;
  const contentBlocks = lastAssistantMessage.content;
  const toolUseBlocks = contentBlocks.filter(b => b.__typename === ToolUseChirpContentBlockTypename);
  if (toolUseBlocks.length === 0) return null;
  return {
    toolUseBlocks,
    toolUseMessageId: lastAssistantMessage.id
  };
}
export function buildToolResults(pendingToolResults, activeTools = []) {
  const results = [];
  for (const [id, result] of pendingToolResults.entries()) {
    var _activeTools$find$too, _activeTools$find;
    const isError = 'type' in result && result.type === 'TOOL_ERROR';
    const content = isError ? result.message : result.content;
    const toolName = (_activeTools$find$too = (_activeTools$find = activeTools.find(tool => tool.toolUseId === id)) === null || _activeTools$find === void 0 ? void 0 : _activeTools$find.toolName) !== null && _activeTools$find$too !== void 0 ? _activeTools$find$too : '';
    results.push({
      toolUseId: id,
      toolName,
      content,
      error: isError,
      __typename: ToolResultChirpContentBlockTypename
    });
  }
  return results;
}